import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faChevronRight, faLocationDot, faLocationCrosshairs } from '@fortawesome/free-solid-svg-icons';
import { Button } from '../../ui';
import { useChainPublic } from './hooks/useChainPublic';
import { useChainAggregate, ChainAggregateByType, ChainAggregateTopShop } from './hooks/useChainAggregate';
import { useChainPublicShops, ChainPublicShop } from './hooks/useChainShops';
import RatingBadge from '../coffee_shops/components/RatingBadge';
import { interpolateRatingColor } from '../coffee_shops/hooks/useCoffeeShops';

const PULL_THRESHOLD_PX = 80;
const PAGE_SIZE = 20;

type SortMode = 'alpha' | 'distance' | 'rating';

interface UserOrigin {
    lat: number;
    lng: number;
}

function formatDistance(meters: number): string {
    if (meters < 1000) return `${meters} m`;
    return `${(meters / 1000).toFixed(1)} km`;
}

function sortShops(shops: ChainPublicShop[], mode: SortMode, hasOrigin: boolean): ChainPublicShop[] {
    const next = [...shops];
    if (mode === 'distance' && hasOrigin) {
        next.sort((a, b) => {
            const da = a.distanceMeters ?? Number.POSITIVE_INFINITY;
            const db = b.distanceMeters ?? Number.POSITIVE_INFINITY;
            return da - db;
        });
    } else if (mode === 'rating') {
        next.sort((a, b) => {
            if (b.avgRating !== a.avgRating) return b.avgRating - a.avgRating;
            return b.ratingCount - a.ratingCount;
        });
    } else {
        next.sort((a, b) => a.name.localeCompare(b.name));
    }
    return next;
}

const ByCoffeeTypeRow: React.FC<{
    entry: ChainAggregateByType;
    maxCount: number;
}> = ({ entry, maxCount }) => {
    const widthPct = maxCount > 0 ? Math.max(4, Math.round((entry.count / maxCount) * 100)) : 0;
    const color = interpolateRatingColor(entry.avgRating);
    return (
        <div className="flex items-center gap-3">
            <div className="w-24 shrink-0 text-sm text-ink truncate" title={entry.coffeeKey}>
                {entry.coffeeKey}
            </div>
            <div className="flex-1 min-w-0">
                <div className="h-2 rounded-full bg-muted/40 overflow-hidden">
                    <div
                        className="h-full rounded-full"
                        style={{ width: `${widthPct}%`, backgroundColor: `${color}99` }}
                    />
                </div>
            </div>
            <div className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                {entry.count} {entry.count === 1 ? 'rating' : 'ratings'}
            </div>
            <div className="text-xs font-semibold tabular-nums whitespace-nowrap" style={{ color }}>
                ★ {entry.avgRating.toFixed(1)}
            </div>
        </div>
    );
};

const TopShopRow: React.FC<{ shop: ChainAggregateTopShop }> = ({ shop }) => {
    const navigate = useNavigate();
    return (
        <button
            type="button"
            onClick={() => navigate(`/coffeeshops/${shop.shopId}`)}
            className="w-full text-left bg-paper/60 backdrop-blur-sm rounded-xl border border-ink/10 p-3 hover:border-ink/25 hover:bg-paper/80 transition-colors flex items-center gap-3"
        >
            <div className="flex-1 min-w-0">
                <h3 className="text-base font-bold text-ink truncate">{shop.name}</h3>
                <div className="text-xs text-muted-foreground mt-0.5">
                    {shop.ratingCount} {shop.ratingCount === 1 ? 'rating' : 'ratings'}
                </div>
            </div>
            <RatingBadge rating={shop.avgRating} size="sm" />
            <FontAwesomeIcon icon={faChevronRight} className="text-muted-foreground text-sm" />
        </button>
    );
};

const AllShopRow: React.FC<{ shop: ChainPublicShop }> = ({ shop }) => {
    const navigate = useNavigate();
    return (
        <button
            type="button"
            onClick={() => navigate(`/coffeeshops/${shop.shopId}`)}
            className="w-full text-left bg-paper/60 backdrop-blur-sm rounded-xl border border-ink/10 p-3 hover:border-ink/25 hover:bg-paper/80 transition-colors flex items-center gap-3"
        >
            <div className="flex-1 min-w-0">
                <h3 className="text-base font-bold text-ink truncate">{shop.name}</h3>
                <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                    {typeof shop.distanceMeters === 'number' && (
                        <>
                            <FontAwesomeIcon icon={faLocationDot} className="text-[10px]" />
                            <span>{formatDistance(shop.distanceMeters)}</span>
                            <span aria-hidden>·</span>
                        </>
                    )}
                    <span className="truncate">{shop.address}</span>
                </div>
            </div>
            {shop.ratingCount > 0 && <RatingBadge rating={shop.avgRating} size="sm" />}
            <FontAwesomeIcon icon={faChevronRight} className="text-muted-foreground text-sm" />
        </button>
    );
};

const ChainPublicPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [origin, setOrigin] = useState<UserOrigin | null>(null);
    const [geoRequested, setGeoRequested] = useState(false);
    const hasOrigin = !!origin;

    const chain = useChainPublic(id);
    const aggregate = useChainAggregate(id);
    const shops = useChainPublicShops(id, { lat: origin?.lat ?? null, lng: origin?.lng ?? null });

    const [sortMode, setSortMode] = useState<SortMode>('alpha');
    const [page, setPage] = useState(1);

    // pull-to-refresh
    const touchStartYRef = useRef<number | null>(null);
    const [pullPx, setPullPx] = useState(0);
    const [refreshing, setRefreshing] = useState(false);

    const refreshAll = useCallback(async () => {
        setRefreshing(true);
        try {
            await Promise.all([chain.refetch(), aggregate.refetch(), shops.refetch()]);
        } finally {
            setRefreshing(false);
        }
    }, [chain, aggregate, shops]);

    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        if (window.scrollY <= 0 && e.touches.length === 1) {
            touchStartYRef.current = e.touches[0].clientY;
        } else {
            touchStartYRef.current = null;
        }
    }, []);

    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        if (touchStartYRef.current == null) return;
        const delta = e.touches[0].clientY - touchStartYRef.current;
        if (delta > 0) setPullPx(Math.min(delta, 120));
    }, []);

    const handleTouchEnd = useCallback(() => {
        if (touchStartYRef.current != null && pullPx > PULL_THRESHOLD_PX) {
            void refreshAll();
        }
        touchStartYRef.current = null;
        setPullPx(0);
    }, [pullPx, refreshAll]);

    const requestGeolocation = useCallback(() => {
        if (!('geolocation' in navigator)) return;
        setGeoRequested(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setOrigin({ lat: pos.coords.latitude, lng: pos.coords.longitude });
            },
            () => {
                /* user denied or unavailable — leave origin null, button stays */
            },
            { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
        );
    }, []);

    // when origin changes, default sort to distance for ergonomics; reset paging.
    useEffect(() => {
        if (hasOrigin) setSortMode('distance');
    }, [hasOrigin]);
    useEffect(() => {
        setPage(1);
    }, [sortMode]);

    const sortedShops = useMemo(
        () => sortShops(shops.data ?? [], sortMode, hasOrigin),
        [shops.data, sortMode, hasOrigin]
    );

    const totalPages = Math.max(1, Math.ceil(sortedShops.length / PAGE_SIZE));
    const pagedShops = sortedShops.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    if (chain.loading && !chain.data) {
        return (
            <div className="max-w-3xl mx-auto px-4 py-6">
                <div className="flex flex-col items-center justify-center py-20">
                    <div className="w-10 h-10 border-4 border-muted border-t-sky rounded-full animate-spin mb-4" />
                    <p className="text-muted-foreground">Loading chain...</p>
                </div>
            </div>
        );
    }

    if ((chain.error && !chain.data) || !chain.data) {
        return (
            <div className="max-w-3xl mx-auto px-4 py-6">
                <Button
                    onClick={() => navigate(-1)}
                    variant="ghost"
                    icon={faArrowLeft}
                    className="mb-6"
                >
                    Back
                </Button>
                <div className="bg-paper/80 backdrop-blur-sm rounded-xl border border-ink/15 shadow-lg p-10 text-center">
                    <h2 className="text-2xl font-bold text-pink mb-2">Chain not found</h2>
                    <p className="text-muted-foreground mb-4">
                        {chain.error || 'We couldn\'t load this chain.'}
                    </p>
                    <Button onClick={chain.refetch} variant="soft">Try Again</Button>
                </div>
            </div>
        );
    }

    const c = chain.data;
    const agg = aggregate.data;
    const accent = c.brandColor || '#3b82f6';
    const visitsLabel = c.totalRatings === 1 ? 'visit' : 'visits';
    const shopsLabel = c.shopCount === 1 ? 'shop' : 'shops';

    const byTypeTop = (agg?.byCoffeeType ?? []).slice(0, 5);
    const byTypeMax = byTypeTop.reduce((m, e) => Math.max(m, e.count), 0);

    const showStaleHint =
        (chain.isStale || aggregate.isStale || shops.isStale) && !refreshing;

    return (
        <div
            className="max-w-3xl mx-auto px-4 py-6"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            {(pullPx > 0 || refreshing) && (
                <div
                    className="flex items-center justify-center text-sm text-muted-foreground mb-2"
                    style={{ height: refreshing ? 32 : Math.min(pullPx, 60) }}
                >
                    {refreshing ? (
                        <div className="w-5 h-5 border-2 border-muted border-t-sky rounded-full animate-spin" />
                    ) : pullPx > PULL_THRESHOLD_PX ? (
                        <span>Release to refresh</span>
                    ) : (
                        <span>Pull to refresh</span>
                    )}
                </div>
            )}

            <Button
                onClick={() => navigate(-1)}
                variant="ghost"
                icon={faArrowLeft}
                className="mb-4"
            >
                Back
            </Button>

            {/* identity */}
            <div className="bg-paper/80 backdrop-blur-sm rounded-xl border border-ink/15 shadow-lg p-5 mb-4">
                <div className="flex items-center gap-4">
                    {c.logoUrl && (
                        <img
                            src={c.logoUrl}
                            alt={`${c.name} logo`}
                            className="w-14 h-14 rounded-lg object-contain bg-paper border border-ink/10"
                        />
                    )}
                    <div className="min-w-0">
                        <h1
                            className="text-3xl font-black leading-tight pb-1 inline-block border-b-4"
                            style={{ borderBottomColor: accent, color: 'inherit' }}
                        >
                            {c.name}
                        </h1>
                        <p className="text-xs text-muted-foreground mt-1">{c.slug}</p>
                    </div>
                </div>

                {/* aggregate */}
                <div className="mt-4 pt-4 border-t border-ink/10 flex items-center gap-3 flex-wrap">
                    {agg && agg.ratingCount > 0 ? (
                        <>
                            <RatingBadge rating={agg.avgRating} size="md" />
                            <span className="text-sm text-ink">
                                {agg.ratingCount} {visitsLabel} across {c.shopCount} {shopsLabel}
                            </span>
                        </>
                    ) : (
                        <span className="text-sm text-muted-foreground">
                            {c.shopCount} {shopsLabel} · no ratings yet
                        </span>
                    )}
                    {showStaleHint && (
                        <span className="px-2 py-1 text-xs text-muted-foreground rounded-full bg-muted/40">
                            refreshing…
                        </span>
                    )}
                </div>
            </div>

            {/* by coffee type */}
            <div className="bg-paper/80 backdrop-blur-sm rounded-xl border border-ink/15 shadow-lg p-5 mb-4">
                <h2 className="text-xl font-bold text-ink mb-1">By coffee type</h2>
                <p className="text-sm text-muted-foreground mb-4">
                    Top types by rating volume across the chain.
                </p>
                {aggregate.loading && byTypeTop.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-2">Loading…</p>
                ) : byTypeTop.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-2">
                        No ratings yet for this chain.
                    </p>
                ) : (
                    <div className="flex flex-col gap-3">
                        {byTypeTop.map((entry) => (
                            <ByCoffeeTypeRow
                                key={entry.coffeeKey}
                                entry={entry}
                                maxCount={byTypeMax}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* top shops */}
            <div className="bg-paper/80 backdrop-blur-sm rounded-xl border border-ink/15 shadow-lg p-5 mb-4">
                <h2 className="text-xl font-bold text-ink mb-1">Top shops</h2>
                <p className="text-sm text-muted-foreground mb-4">
                    Best-rated locations in this chain.
                </p>
                {agg && agg.topShops.length > 0 ? (
                    <div className="flex flex-col gap-2">
                        {agg.topShops.map((s) => (
                            <TopShopRow key={s.shopId} shop={s} />
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-muted-foreground py-2">
                        Not enough ratings to rank shops yet.
                    </p>
                )}
            </div>

            {/* all shops */}
            <div className="bg-paper/80 backdrop-blur-sm rounded-xl border border-ink/15 shadow-lg p-5 mb-4">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <h2 className="text-xl font-bold text-ink">All shops</h2>
                    {!hasOrigin && (
                        <Button
                            onClick={requestGeolocation}
                            variant="soft"
                            icon={faLocationCrosshairs}
                            className="text-xs"
                        >
                            {geoRequested ? 'Use my location' : 'Show distance'}
                        </Button>
                    )}
                </div>

                <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className="text-xs text-muted-foreground">Sort:</span>
                    <button
                        type="button"
                        onClick={() => setSortMode('alpha')}
                        className={`text-xs px-2 py-1 rounded-full border ${
                            sortMode === 'alpha'
                                ? 'bg-ink text-paper border-ink'
                                : 'bg-paper text-ink border-ink/20 hover:border-ink/40'
                        }`}
                    >
                        A–Z
                    </button>
                    <button
                        type="button"
                        onClick={() => setSortMode('distance')}
                        disabled={!hasOrigin}
                        className={`text-xs px-2 py-1 rounded-full border ${
                            sortMode === 'distance'
                                ? 'bg-ink text-paper border-ink'
                                : 'bg-paper text-ink border-ink/20 hover:border-ink/40 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-ink/20'
                        }`}
                    >
                        Distance
                    </button>
                    <button
                        type="button"
                        onClick={() => setSortMode('rating')}
                        className={`text-xs px-2 py-1 rounded-full border ${
                            sortMode === 'rating'
                                ? 'bg-ink text-paper border-ink'
                                : 'bg-paper text-ink border-ink/20 hover:border-ink/40'
                        }`}
                    >
                        Rating
                    </button>
                </div>

                {shops.loading && sortedShops.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-2">Loading shops…</p>
                ) : sortedShops.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-2">
                        No shops listed yet.
                    </p>
                ) : (
                    <>
                        <div className="flex flex-col gap-2">
                            {pagedShops.map((s) => (
                                <AllShopRow key={s.shopId} shop={s} />
                            ))}
                        </div>
                        {totalPages > 1 && (
                            <div className="mt-4 flex items-center justify-between text-sm">
                                <Button
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    variant="ghost"
                                    disabled={page <= 1}
                                >
                                    Prev
                                </Button>
                                <span className="text-muted-foreground">
                                    {page} / {totalPages}
                                </span>
                                <Button
                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                    variant="ghost"
                                    disabled={page >= totalPages}
                                >
                                    Next
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ChainPublicPage;
